import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type { Criticality, LifecycleState, RiskTier, Vendor, VendorFilter } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, vendorApi } from "../../lib/api";
import { VendorDemoBanner } from "../../components/VendorMode";
import {
  CRITICALITIES,
  CRITICALITY_LABEL,
  LIFECYCLE_STATES,
  RISK_TIERS,
  STATE_LABEL,
  TIER_LABEL,
  stateTone,
  tierTone,
} from "./labels";

function withFilter<K extends keyof VendorFilter>(
  filter: VendorFilter,
  key: K,
  value: VendorFilter[K] | undefined,
): VendorFilter {
  const next = { ...filter };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}

export function VenVendors() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [filter, setFilter] = useState<VendorFilter>({});
  const [registering, setRegistering] = useState(false);

  const members = useAsync<{ id: string; email: string; displayName: string }[]>(
    async () => (await api.members(orgId)).members,
    [orgId],
  );
  const personName = (id: string | null | undefined): string => {
    if (!id) return "Unassigned";
    const match = (members.data ?? []).find((m) => m.id === id);
    return match ? match.displayName || match.email : id;
  };

  const state = useAsync(
    async () => {
      const [vendors, categories] = await Promise.all([
        vendorApi.vendors(orgId, filter),
        vendorApi.categories(orgId),
      ]);
      return { vendors, categories };
    },
    [orgId, filter.riskTier ?? "", filter.lifecycleState ?? "", filter.search ?? ""],
  );

  const vendors = state.data?.vendors.data.vendors ?? [];
  const categories = state.data?.categories.data.categories ?? [];
  const demoMode = state.data?.vendors.demoMode ?? false;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Vendor register</h1>
          <p>Every third party this organization has registered, and the state each one is in.</p>
        </div>
        <Button size="sm" onClick={() => setRegistering(true)} disabled={!orgId}>
          Register a vendor
        </Button>
      </div>

      <VendorDemoBanner show={demoMode} />

      <Card>
        <CardHeader title="Filter" />
        <CardBody>
          <div className="form-grid">
            <Field label="Risk tier">
              {(id) => (
                <Select
                  id={id}
                  value={filter.riskTier ?? ""}
                  onChange={(e) =>
                    setFilter((f) => withFilter(f, "riskTier", (e.target.value || undefined) as RiskTier | undefined))
                  }
                >
                  <option value="">Any tier</option>
                  {RISK_TIERS.map((t) => (
                    <option key={t} value={t}>
                      {TIER_LABEL[t]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Lifecycle state">
              {(id) => (
                <Select
                  id={id}
                  value={filter.lifecycleState ?? ""}
                  onChange={(e) =>
                    setFilter((f) =>
                      withFilter(f, "lifecycleState", (e.target.value || undefined) as LifecycleState | undefined),
                    )
                  }
                >
                  <option value="">Any state</option>
                  {LIFECYCLE_STATES.map((s) => (
                    <option key={s} value={s}>
                      {STATE_LABEL[s]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Name contains">
              {(id) => (
                <TextInput
                  id={id}
                  value={filter.search ?? ""}
                  onChange={(e) => setFilter((f) => withFilter(f, "search", e.target.value || undefined))}
                  placeholder="Legal or trading name"
                />
              )}
            </Field>
          </div>
        </CardBody>
      </Card>

      {state.loading && <LoadingState label="Loading the vendor register…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data && vendors.length === 0 && (
        <EmptyState
          title="No vendors match"
          description="Clear the filters, or register the first third party you buy from."
        />
      )}
      {vendors.length > 0 && (
        <Card>
          <CardBody>
            <DataTable
              columns={[
                {
                  key: "legalName",
                  header: "Vendor",
                  render: (v: Vendor) => <Link to={`/studio/vendor-ai/vendors/${v.id}`}>{v.legalName}</Link>,
                },
                {
                  key: "riskTier",
                  header: "Risk tier",
                  render: (v: Vendor) => <Badge tone={tierTone(v.riskTier)}>{TIER_LABEL[v.riskTier]}</Badge>,
                },
                {
                  key: "lifecycleState",
                  header: "State",
                  render: (v: Vendor) => (
                    <Badge tone={stateTone(v.lifecycleState)}>{STATE_LABEL[v.lifecycleState]}</Badge>
                  ),
                },
                {
                  key: "owner",
                  header: "Owner",
                  render: (v: Vendor) => personName(v.owner?.ownerUserId),
                },
                { key: "createdAt", header: "Registered", render: (v: Vendor) => formatDate(v.createdAt) },
              ]}
              rows={vendors}
              caption="Vendor register"
            />
          </CardBody>
        </Card>
      )}

      <RegisterVendorDialog
        open={registering}
        orgId={orgId}
        categories={categories}
        onClose={() => setRegistering(false)}
        onRegistered={() => {
          setRegistering(false);
          state.reload();
        }}
      />
    </>
  );
}

function RegisterVendorDialog({
  open,
  orgId,
  categories,
  onClose,
  onRegistered,
}: {
  open: boolean;
  orgId: string;
  categories: { id: string; key: string; label: string }[];
  onClose: () => void;
  onRegistered: () => void;
}) {
  const [legalName, setLegalName] = useState("");
  const [tradingName, setTradingName] = useState("");
  const [website, setWebsite] = useState("");
  const [categoryKey, setCategoryKey] = useState("");
  const [riskTier, setRiskTier] = useState<RiskTier>("TIER_3");
  const [criticality, setCriticality] = useState<Criticality>("MODERATE");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await vendorApi.createVendor(orgId, {
        legalName: legalName.trim(),
        tradingName: tradingName.trim() || null,
        website: website.trim() || null,
        categoryKey,
        riskTier,
        criticality,
      });
      setLegalName("");
      setTradingName("");
      setWebsite("");
      onRegistered();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not register this vendor.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      title="Register a vendor"
      description="Registration records who the vendor is. Classification, ownership and approval are separate governed steps."
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || legalName.trim().length === 0 || categoryKey === ""}>
            {saving ? "Registering…" : "Register"}
          </Button>
        </>
      }
    >
      {error && <ErrorState description={error} />}
      <div className="form-grid">
        <Field label="Legal name">
          {(id) => <TextInput id={id} value={legalName} onChange={(e) => setLegalName(e.target.value)} />}
        </Field>
        <Field label="Trading name" help="Optional. The name people actually use for them.">
          {(id) => <TextInput id={id} value={tradingName} onChange={(e) => setTradingName(e.target.value)} />}
        </Field>
        <Field label="Website">
          {(id) => <TextInput id={id} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" />}
        </Field>
        <Field label="Category">
          {(id) => (
            <Select id={id} value={categoryKey} onChange={(e) => setCategoryKey(e.target.value)}>
              <option value="">Choose a category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.key}>
                  {c.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Risk tier"
          help="A first assessment. Classification history keeps every later change and the reason for it."
        >
          {(id) => (
            <Select id={id} value={riskTier} onChange={(e) => setRiskTier(e.target.value as RiskTier)}>
              {RISK_TIERS.map((t) => (
                <option key={t} value={t}>
                  {TIER_LABEL[t]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Criticality">
          {(id) => (
            <Select id={id} value={criticality} onChange={(e) => setCriticality(e.target.value as Criticality)}>
              {CRITICALITIES.map((c) => (
                <option key={c} value={c}>
                  {CRITICALITY_LABEL[c]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    </Dialog>
  );
}
